import React, { useState, useEffect } from 'react';
import './HRHolidays.css';

const BASE = 'https://fotileams.com/api';

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  };
}

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const PAKISTAN_HOLIDAYS_2026 = [
  { date: '2026-01-01', name: "New Year's Day" },
  { date: '2026-03-23', name: 'Pakistan Day' },
  { date: '2026-05-01', name: 'Labour Day' },
  { date: '2026-08-14', name: 'Independence Day' },
  { date: '2026-11-09', name: 'Iqbal Day' },
  { date: '2026-12-25', name: "Quaid-e-Azam Day" },
];

export default function HRHolidays() {
  const [holidays, setHolidays]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [year, setYear]           = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState('');
  const [holidayName, setHolidayName]   = useState('');
  const [adding, setAdding]       = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [activeMonth, setActiveMonth] = useState(new Date().getMonth());

  useEffect(() => { fetchHolidays(); }, [year]);

  const fetchHolidays = async () => {
    try {
      setLoading(true);
      const res  = await fetch(`${BASE}/holidays/?year=${year}`, { headers: authHeaders() });
      const data = await res.json();
      setHolidays(data.holidays || []);
    } catch {} finally { setLoading(false); }
  };

  const addHoliday = async () => {
    if (!selectedDate || !holidayName.trim()) {
      setError('Date and name are required');
      return;
    }
    setAdding(true); setError(''); setSuccess('');
    try {
      const res  = await fetch(`${BASE}/holidays/`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ date: selectedDate, name: holidayName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed'); return; }
      setSuccess(`Holiday "${holidayName}" added for ${selectedDate}`);
      setHolidayName(''); setSelectedDate('');
      fetchHolidays();
    } catch { setError('Server error'); }
    finally { setAdding(false); }
  };

  const deleteHoliday = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from holidays?`)) return;
    try {
      await fetch(`${BASE}/holidays/${id}`, { method: 'DELETE', headers: authHeaders() });
      fetchHolidays();
    } catch {}
  };

  const addPreset = async (preset) => {
    try {
      await fetch(`${BASE}/holidays/`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ date: preset.date, name: preset.name }),
      });
      fetchHolidays();
    } catch {}
  };

  // Build calendar for active month
  const buildCalendar = () => {
    const firstDay   = new Date(year, activeMonth, 1).getDay();
    const daysInMonth = new Date(year, activeMonth + 1, 0).getDate();
    const holidayMap  = {};
    holidays.forEach(h => {
      const d = h.date.split('T')[0];
      holidayMap[d] = h;
    });

    const cells = [];
    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) cells.push(null);
    // Days
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(activeMonth+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      const dow     = new Date(dateStr).getDay();
      cells.push({ day: d, dateStr, dow, holiday: holidayMap[dateStr] });
    }
    return cells;
  };

  const cells = buildCalendar();
  const holidayDates = new Set(holidays.map(h => h.date.split('T')[0]));

  return (
    <div className="hh-root">
      <div className="hh-header">
        <h1 className="hh-title">Public Holidays</h1>
        <div className="hh-year-pills">
          {[2025,2026,2027].map(y => (
            <button key={y} className={`hh-year-pill ${year===y?'hh-year-active':''}`}
              onClick={() => setYear(y)}>{y}</button>
          ))}
        </div>
      </div>

      {/* Add Holiday Form */}
      <div className="hh-add-card">
        <div className="hh-add-title">Add Public Holiday</div>
        {error   && <div className="hh-msg hh-err">{error}</div>}
        {success && <div className="hh-msg hh-ok">{success}</div>}
        <div className="hh-add-row">
          <input type="date" className="hh-date-input"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            min={`${year}-01-01`} max={`${year}-12-31`}
          />
          <input type="text" className="hh-name-input"
            placeholder="Holiday name (e.g. Eid ul Fitr)"
            value={holidayName}
            onChange={e => setHolidayName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addHoliday()}
          />
          <button className="hh-add-btn" onClick={addHoliday} disabled={adding}>
            {adding ? 'Adding…' : '+ Add'}
          </button>
        </div>
      </div>

      {/* Preset Pakistan holidays */}
      <div className="hh-presets">
        <div className="hh-presets-label">Quick Add Pakistan Holidays {year}:</div>
        <div className="hh-presets-list">
          {PAKISTAN_HOLIDAYS_2026.map((p, i) => (
            <button
              key={i}
              className={`hh-preset-pill ${holidayDates.has(p.date) ? 'hh-preset-added' : ''}`}
              onClick={() => !holidayDates.has(p.date) && addPreset(p)}
              disabled={holidayDates.has(p.date)}
            >
              {holidayDates.has(p.date) ? '✓ ' : '+ '}{p.name}
            </button>
          ))}
        </div>
      </div>

      {/* Calendar */}
      <div className="hh-calendar-card">
        <div className="hh-month-nav">
          <button className="hh-month-btn" onClick={() => setActiveMonth(m => Math.max(0, m-1))}>‹</button>
          <span className="hh-month-title">{MONTHS[activeMonth]} {year}</span>
          <button className="hh-month-btn" onClick={() => setActiveMonth(m => Math.min(11, m+1))}>›</button>
        </div>

        <div className="hh-cal-grid">
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
            <div key={d} className="hh-cal-hd">{d}</div>
          ))}
          {cells.map((cell, i) => {
            if (!cell) return <div key={`e${i}`} className="hh-cal-empty" />;
            const isSun     = cell.dow === 0;
            const isHoliday = !!cell.holiday;
            const isToday   = cell.dateStr === new Date().toISOString().split('T')[0];

            return (
              <div
                key={i}
                className={`hh-cal-day
                  ${isSun ? 'hh-cal-sun' : ''}
                  ${isHoliday ? 'hh-cal-holiday' : ''}
                  ${isToday ? 'hh-cal-today' : ''}
                `}
                title={cell.holiday?.name || (isSun ? 'Sunday' : '')}
                onClick={() => {
                  if (!isSun && !isHoliday) {
                    setSelectedDate(cell.dateStr);
                    setHolidayName('');
                  }
                }}
              >
                <span className="hh-cal-num">{cell.day}</span>
                {isHoliday && <span className="hh-cal-dot">●</span>}
              </div>
            );
          })}
        </div>

        <div className="hh-cal-legend">
          <span className="hh-leg hh-leg-holiday">● Holiday</span>
          <span className="hh-leg hh-leg-sun">■ Sunday</span>
          <span className="hh-leg hh-leg-today">○ Today</span>
        </div>
      </div>

      {/* Holiday list */}
      <div className="hh-list-title">{year} Holidays ({holidays.length})</div>

      {loading ? (
        <div className="hh-loading">Loading…</div>
      ) : holidays.length === 0 ? (
        <div className="hh-empty">No holidays added for {year} yet</div>
      ) : (
        <div className="hh-list">
          {holidays.map((h, i) => {
            const d   = new Date(h.date);
            const dow = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][d.getDay()];
            return (
              <div key={i} className="hh-item">
                <div className="hh-item-dot" />
                <div className="hh-item-info">
                  <div className="hh-item-name">{h.name}</div>
                  <div className="hh-item-date">
                    {dow}, {d.toLocaleDateString('en-PK', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                </div>
                <button className="hh-del-btn" onClick={() => deleteHoliday(h.id, h.name)}>✕</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}